'use client'

import { useEffect, useRef } from 'react'

/**
 * Fundo animado da tela de login (ocupa a tela toda): 3 pares de curvas (espelhadas em
 * cima e embaixo) saem da borda esquerda e se juntam numa linha reta no meio.
 * Cada curva tem um brilho borrado recortado só do lado de fora, e pontos
 * percorrem as curvas devagar nas pontas e rápido no meio — é isso que dá a
 * sensação de pulso.
 *
 * Versão avulsa (abre direto no navegador) em /fluxo-login.html na raiz do repo.
 */

/**
 * Cores: as da animação original (/fluxo-login.html na raiz). Fixas — não
 * seguem a cor da marca. Troque aqui.
 */
const COR_FUNDO = '#0f1a2f'           // azul-marinho
const COR_LINHA = '#7FC8FF'           // traço fino e luz borrada das curvas
const COR_PONTO = 'rgb(122,192,244)'  // pontos
const HALO = '127,200,255'            // halo dos pontos (r,g,b)

const OPCOES = {
  encontro: 0.147, // onde as curvas encontram a reta (fração da largura)
  controle: 0.052, // quão "aberta" a curva chega (fração da largura)
  raioPonto: 6,
  raioHalo: 64,
}

// startY: fração da metade da altura; esmaecer: até onde vai o brilho (fração da largura)
const CURVAS = [
  { startY: 0.0, brilho: 20, borrao: 50, esmaecer: 0.345, duracao: 12 },
  { startY: 0.5, brilho: 15, borrao: 20, esmaecer: 0.21, duracao: 10 },
  { startY: 0.9, brilho: 10, borrao: 15, esmaecer: 0.14, duracao: 14 },
]

const NS = 'http://www.w3.org/2000/svg'

interface Trilha {
  pontos: { x: number; y: number }[]
  recorte: Path2D
  atraso: number
  duracao: number
}

interface Particula {
  trilha: number
  comeco: number
  duracao: number
  viagem: number
}

function el(tag: string, attrs: Record<string, string | number>, pai?: Element) {
  const e = document.createElementNS(NS, tag)
  for (const k in attrs) e.setAttribute(k, String(attrs[k]))
  if (pai) pai.appendChild(e)
  return e
}

// ease-in-out (cubic-bezier .42,0,.58,1) seguido de smoothstep
function suave(x: number) {
  let lo = 0
  let hi = 1
  let t = 0.5
  for (let i = 0; i < 12; i++) {
    t = (lo + hi) / 2
    const v = 3 * (1 - t) ** 2 * t * 0.42 + 3 * (1 - t) * t * t * 0.58 + t ** 3
    if (v < x) lo = t
    else hi = t
  }
  t = (lo + hi) / 2
  return t * t * (3 - 2 * t)
}

function degrau(a: number, b: number, x: number) {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/**
 * Versão do celular (abaixo de lg): uma linha reta na parte de baixo da tela,
 * com pontos passando devagar nas pontas e rápido no meio, e o halo pulsando.
 * CSS puro + Web Animations — sem canvas, que no celular não vale o custo.
 */
function LinhaCelular() {
  const pontosRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const animacoes = pontosRef.current.flatMap((el, i) => {
      if (!el) return []
      const duracao = 7000 + i * 900
      const atraso = i * 2300
      return [
        el.animate(
          [
            { left: '-8%', opacity: 0, offset: 0 },
            { opacity: 1, offset: 0.05 },
            { opacity: 1, offset: 0.75 },
            { left: '108%', opacity: 0, offset: 1 },
          ],
          { duration: duracao, delay: atraso, iterations: Infinity, easing: 'cubic-bezier(.42,0,.58,1)', fill: 'backwards' },
        ),
        (el.firstElementChild as HTMLElement).animate(
          [{ transform: 'translate(-50%,-50%) scale(0.8)' }, { transform: 'translate(-50%,-50%) scale(1.25)' }],
          { duration: 1400, iterations: Infinity, direction: 'alternate', easing: 'ease-in-out' },
        ),
      ]
    })
    return () => animacoes.forEach((a) => a.cancel())
  }, [])

  return (
    <div className="lg:hidden" style={{ position: 'absolute', left: 0, right: 0, bottom: '9%', height: 1 }}>
      {/* a linha e o brilho leve acima e abaixo dela */}
      <div style={{ position: 'absolute', inset: 0, background: COR_LINHA }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: '100%', height: 40, background: `linear-gradient(to top, rgba(${HALO},0.08), transparent)` }} />
      <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', height: 40, background: `linear-gradient(to bottom, rgba(${HALO},0.08), transparent)` }} />
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          ref={(el) => { pontosRef.current[i] = el }}
          style={{ position: 'absolute', top: 0, left: '-8%', width: 0, height: 0, opacity: 0 }}
        >
          {/* halo que pulsa */}
          <div style={{ position: 'absolute', left: 0, top: 0, width: 128, height: 128, borderRadius: '50%', transform: 'translate(-50%,-50%)', background: `radial-gradient(circle, rgba(${HALO},0.3), rgba(${HALO},0) 70%)` }} />
          {/* ponto */}
          <div style={{ position: 'absolute', left: 0, top: 0, width: 12, height: 12, borderRadius: '50%', transform: 'translate(-50%,-50%)', background: COR_PONTO }} />
        </div>
      ))}
    </div>
  )
}

export default function FluxoAnimado() {
  const caixaRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const caixa = caixaRef.current
    const svg = svgRef.current
    const cv = canvasRef.current
    const ctx = cv?.getContext('2d')
    if (!caixa || !svg || !cv || !ctx) return

    const reduzir = matchMedia('(prefers-reduced-motion: reduce)').matches
    const inicio = performance.now()
    let W = 0
    let H = 0
    let trilhas: Trilha[] = []
    let particulas: Particula[] = []
    let quadroId = 0

    function amostrar(d: string) {
      const p = el('path', { d }, svg!) as SVGPathElement
      const total = p.getTotalLength()
      const out: { x: number; y: number }[] = []
      for (let i = 0; i <= 256; i++) {
        const q = p.getPointAtLength((total * i) / 256)
        out.push({ x: q.x, y: q.y })
      }
      p.remove()
      return out
    }

    function montar() {
      W = caixa!.clientWidth
      H = caixa!.clientHeight
      const meio = H / 2
      svg!.setAttribute('viewBox', `0 0 ${W} ${H}`)
      svg!.replaceChildren()
      const defs = el('defs', {}, svg!)
      trilhas = []

      // curva: sai da borda esquerda em y0, chega horizontal em (mx, meio) e segue reta até W
      const curvaD = (y0: number, cx: number, mx: number) =>
        `M 0 ${y0} C 0 ${y0} ${cx} ${meio} ${mx} ${meio} L ${W} ${meio}`
      // região do lado de fora da curva (entre ela e a borda de cima/baixo)
      const mascaraD = (borda: number, y0: number, cx: number, mx: number) =>
        `M ${mx} ${meio} C ${cx} ${meio} 0 ${y0} 0 ${y0} V ${borda} H ${W} V ${meio} H ${mx} Z`

      CURVAS.forEach((c, i) => {
        const cx = OPCOES.controle * W
        const mx = (OPCOES.encontro - 0.025 * i) * W
        const yTopo = c.startY * meio

        el('filter', { id: `fx-f${i}`, x: '-50%', y: '-100%', width: '200%', height: '300%',
          'color-interpolation-filters': 'sRGB' }, defs)
          .appendChild(el('feGaussianBlur', { stdDeviation: c.borrao }))
        const g = el('linearGradient', { id: `fx-g${i}`, x1: 0, y1: 0, x2: W, y2: 0,
          gradientUnits: 'userSpaceOnUse' }, defs)
        el('stop', { 'stop-color': COR_LINHA }, g)
        el('stop', { offset: c.esmaecer, 'stop-color': COR_LINHA, 'stop-opacity': 0 }, g)

        const metades = [
          { chave: 't', y0: yTopo, borda: 0, atraso: 0 },
          { chave: 'b', y0: H - yTopo, borda: H, atraso: i * 0.2 + 3 + c.duracao / 2 },
        ]
        for (const m of metades) {
          const mascara = mascaraD(m.borda, m.y0, cx, mx)
          const mk = el('mask', { id: `fx-m${m.chave}${i}`, maskUnits: 'userSpaceOnUse',
            x: 0, y: 0, width: W, height: H, style: 'mask-type:alpha' }, defs)
          el('path', { d: mascara, fill: '#fff' }, mk)

          const d = curvaD(m.y0, cx, mx)
          el('path', { d, stroke: `url(#fx-g${i})`, 'stroke-width': c.brilho, fill: 'none',
            filter: `url(#fx-f${i})` }, el('g', { mask: `url(#fx-m${m.chave}${i})` }, svg!))
          el('path', { d, stroke: COR_LINHA, fill: 'none' }, svg!)

          trilhas.push({ pontos: amostrar(d), recorte: new Path2D(mascara), atraso: m.atraso, duracao: c.duracao * 1.5 })
        }
      })

      const dpr = Math.min(devicePixelRatio || 1, 2)
      cv!.width = W * dpr
      cv!.height = H * dpr
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)

      // 3 partículas por trilha, cada uma entrando um pouco depois da outra
      particulas = Array.from({ length: trilhas.length * 3 }, (_, n) => {
        const t = Math.floor(Math.random() * trilhas.length)
        return {
          trilha: t,
          comeco: trilhas[t].atraso + n * 0.45,
          duracao: trilhas[t].duracao * (0.8 + 0.4 * Math.random()),
          viagem: 0,
        }
      })
    }

    function quadro(agora: number) {
      // escondida (celular): não desenha, só espera a tela crescer
      if (cv!.clientWidth === 0) {
        if (!reduzir) quadroId = requestAnimationFrame(quadro)
        return
      }
      const tempo = reduzir ? 6 : (agora - inicio) / 1000
      ctx!.clearRect(0, 0, W, H)

      for (const p of particulas) {
        if (tempo < p.comeco) continue
        const decorrido = tempo - p.comeco
        const viagem = Math.floor(decorrido / p.duracao)
        // ao terminar uma viagem, sorteia outra trilha
        if (viagem !== p.viagem) {
          p.viagem = viagem
          p.trilha = Math.floor(Math.random() * trilhas.length)
        }
        const fase = (decorrido / p.duracao) % 1
        const tr = trilhas[p.trilha]

        const idx = suave(fase) * 256
        const i = Math.min(Math.floor(idx), 255)
        const f = idx - i
        const a = tr.pontos[i]
        const b = tr.pontos[i + 1]
        const x = a.x + (b.x - a.x) * f
        const y = a.y + (b.y - a.y) * f
        const opacidade = 0.3 * degrau(0, 0.05, fase) * (1 - degrau(0.75, 1, fase))

        // halo: queda linear, só do lado de fora da curva
        const R = OPCOES.raioHalo
        const g = ctx!.createRadialGradient(x, y, 0, x, y, R)
        g.addColorStop(0, `rgba(${HALO},${opacidade})`)
        g.addColorStop(1, `rgba(${HALO},0)`)
        ctx!.save()
        ctx!.clip(tr.recorte)
        ctx!.fillStyle = g
        ctx!.fillRect(x - R, y - R, R * 2, R * 2)
        ctx!.restore()

        ctx!.fillStyle = COR_PONTO
        ctx!.beginPath()
        ctx!.arc(x, y, OPCOES.raioPonto, 0, Math.PI * 2)
        ctx!.fill()
      }
      if (!reduzir) quadroId = requestAnimationFrame(quadro)
    }

    montar()
    quadroId = requestAnimationFrame(quadro)
    const obs = new ResizeObserver(montar)
    obs.observe(caixa)
    return () => {
      cancelAnimationFrame(quadroId)
      obs.disconnect()
    }
  }, [])

  return (
    <div ref={caixaRef} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: COR_FUNDO, pointerEvents: 'none' }}>
      {/* A animação das curvas só entra a partir de lg (1024px); abaixo disso
          fica só uma linha pulsando na parte de baixo (LinhaCelular). */}
      <svg ref={svgRef} xmlns={NS} className="hidden lg:block" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      <canvas ref={canvasRef} className="hidden lg:block" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      <LinhaCelular />
    </div>
  )
}
